/**
 * Plan Context Hook - Fetches real-time plan context from Contex
 *
 * This hook provides access to all the context data for a plan,
 * including tech stack, decomposed tasks, features, events, commands, etc.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { queryKeys } from '../../lib/query-client';
import { useWebSocket } from '../../contexts/WebSocketContext';
import apiClient from '../../services/unifiedApiClient';

/**
 * Fetch plan context from the API
 * @param {string} planId - The plan/task ID
 * @returns {Promise<Object>} Plan context data
 */
const fetchPlanContext = async (planId) => {
  const response = await apiClient.get(`/plans/${planId}/context`);
  return response;
};

/**
 * Hook to fetch and subscribe to plan context updates
 *
 * @param {string} planId - The plan/task ID
 * @param {Object} options - Query options
 * @param {boolean} options.enabled - Whether to enable the query (default: true)
 * @param {number} options.refetchInterval - Auto-refetch interval in ms (default: disabled)
 * @returns {Object} Query result with context data
 */
export const usePlanContext = (planId, options = {}) => {
  const queryClient = useQueryClient();
  const { subscribe, connected } = useWebSocket();

  const {
    enabled = true,
    refetchInterval = false,
    ...queryOptions
  } = options;

  // Subscribe to WebSocket updates for this plan
  useEffect(() => {
    if (!connected || !planId) return;

    const unsubscribe = subscribe((data) => {
      // Refetch context when we get updates for this plan
      if (data.task_id === planId || data.plan_id === planId) {
        // Only refetch on certain event types
        const relevantEvents = [
          'task.created',
          'task.updated',
          'decomposition.completed',
          'event_modeling.completed',
          'context.updated',
          'plan.status_changed',
          'plan.completed',
        ];

        if (relevantEvents.includes(data.type)) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.plans.context(planId),
          });
        }
      }
    });

    return () => unsubscribe();
  }, [connected, subscribe, planId, queryClient]);

  return useQuery({
    queryKey: queryKeys.plans.context(planId),
    queryFn: () => fetchPlanContext(planId),
    enabled: enabled && !!planId,
    refetchInterval,
    // Keep previous data while fetching new data
    placeholderData: (previousData) => previousData,
    ...queryOptions,
  });
};

/**
 * Hook to get just the processing status of a plan
 * Useful for showing loading states during decomposition
 */
export const usePlanStatus = (planId) => {
  const { data, isLoading, error } = usePlanContext(planId, {
    // Poll every 2 seconds while processing
    refetchInterval: (data) => {
      return data?.is_processing ? 2000 : false;
    },
  });

  return {
    isProcessing: data?.is_processing ?? true,
    status: data?.task_status ?? 'unknown',
    stats: data?.stats ?? {},
    isLoading,
    error,
  };
};

export default usePlanContext;
